import React, { useEffect, useRef, useState } from 'react';

const NAVY = '#1B1642';
const INK = '#3F3F6E';
const YELLOW = '#fce76b';
const MINT = '#B8ECD7';
const MIST = '#f6f6f6';

const useIsMobile = (breakpoint = 1024) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const STEPS = [
  {
    title: 'They order for tomorrow',
    text: 'New customers find you through a NextDay deal, up to 60% off.',
  },
  {
    title: "You prep with a day's notice",
    text: 'Orders arrive in advance, so you cook in planned batches with less waste.',
  },
  {
    title: 'They return at full price',
    text: 'One great meal turns a stranger into a repeat order.',
  },
];

const ORDERS = [
  { label: 'Order 1', sub: 'First try', amount: '$9.60', width: 40, trial: true },
  { label: 'Order 2', sub: 'Full price', amount: '$24.00', width: 100 },
  { label: 'Order 3', sub: 'Full price', amount: '$24.00', width: 100 },
  { label: 'Order 4', sub: 'Full price', amount: '$24.00', width: 100 },
];

const zigzagMask =
  'radial-gradient(circle 8px at 50% 100%, transparent 98%, #000) 0 0 / 22px 100% repeat-x';

const HomeTopicThree = () => {
  const isMobile = useIsMobile();
  const receiptRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Fill the bars once the receipt scrolls into view
  useEffect(() => {
    const node = receiptRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const rule = { borderTop: '1.5px dashed #b9b8c9', margin: '18px 0' };

  return (
    <section style={{ backgroundColor: '#fff', width: '100%' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.05fr 1fr',
          gap: isMobile ? '36px' : '80px',
          alignItems: 'center',
          maxWidth: '1280px',
          margin: '0 auto',
          padding: isMobile ? '48px 22px' : '110px 120px',
          boxSizing: 'border-box',
        }}
      >
        {/* Text */}
        <div>
          <h2
            style={{
              margin: 0,
              color: NAVY,
              fontFamily: 'Inter Bold',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontSize: isMobile ? '32px' : '52px',
              lineHeight: isMobile ? 1.1 : 1.05,
            }}
          >
            Turn a first bite into a regular customer
          </h2>
          <p
            style={{
              margin: '18px 0 0',
              color: INK,
              fontFamily: 'Inter Regular',
              fontSize: isMobile ? '17px' : '20px',
              lineHeight: 1.5,
              maxWidth: '30em',
            }}
          >
            With the NextDay Meal Program, customers order a day ahead and save up to 60% on their
            first taste of your food. When they love it, they come back at full price.
          </p>

          <ol
            style={{
              listStyle: 'none',
              margin: '36px 0 0',
              padding: 0,
              borderTop: `1.5px solid ${NAVY}`,
            }}
          >
            {STEPS.map((step, i) => (
              <li
                key={step.title}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '44px 1fr',
                  gap: '14px',
                  padding: '16px 0',
                  borderBottom: '1px solid #dcdbe6',
                }}
              >
                <span
                  style={{
                    fontFamily: 'Inter Bold',
                    fontWeight: 800,
                    fontSize: '22px',
                    color: NAVY,
                  }}
                >
                  {i + 1}
                </span>
                <div>
                  <div
                    style={{
                      fontFamily: 'Inter Bold',
                      fontWeight: 600,
                      fontSize: '17px',
                      color: NAVY,
                    }}
                  >
                    {step.title}
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter Regular',
                      fontSize: '15px',
                      lineHeight: 1.45,
                      color: INK,
                      marginTop: '2px',
                    }}
                  >
                    {step.text}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Receipt */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            ref={receiptRef}
            aria-label="Example of one customer's orders"
            style={{
              width: '100%',
              maxWidth: '400px',
              backgroundColor: MIST,
              padding: '28px 26px 34px',
              boxSizing: 'border-box',
              fontVariantNumeric: 'tabular-nums',
              color: NAVY,
              transform: isMobile ? 'none' : 'rotate(1.5deg)',
              WebkitMask: zigzagMask,
              mask: zigzagMask,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontFamily: 'Inter Regular',
                fontSize: '13px',
                color: INK,
              }}
            >
              <span>Example customer</span>
              <span>Your store</span>
            </div>
            <div
              style={{
                fontFamily: 'Inter Bold',
                fontWeight: 700,
                fontSize: '20px',
                marginTop: '6px',
              }}
            >
              One customer, four orders
            </div>
            <div style={rule} />

            {ORDERS.map((o, i) => (
              <div
                key={o.label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'end',
                  gap: '12px',
                  padding: '10px 0',
                }}
              >
                <div>
                  <div style={{ fontFamily: 'Inter Bold', fontWeight: 600, fontSize: '15px' }}>
                    {o.label}
                    {o.trial && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginLeft: '6px',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          backgroundColor: YELLOW,
                          fontSize: '12px',
                          fontWeight: 700,
                          verticalAlign: '2px',
                        }}
                      >
                        NextDay −60%
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter Regular',
                      fontSize: '13px',
                      color: INK,
                      marginTop: '2px',
                    }}
                  >
                    {o.sub}
                  </div>
                </div>
                <div style={{ fontFamily: 'Inter Bold', fontWeight: 700, fontSize: '17px' }}>
                  {o.amount}
                </div>
                <div
                  style={{
                    gridColumn: '1 / -1',
                    height: '8px',
                    backgroundColor: '#e2e1ea',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginTop: '6px',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      borderRadius: '4px',
                      backgroundColor: o.trial ? MINT : NAVY,
                      width: visible ? `${o.width}%` : '0%',
                      transition: `width 0.9s cubic-bezier(.2,.7,.2,1) ${i * 0.15}s`,
                    }}
                  />
                </div>
              </div>
            ))}

            <div style={rule} />
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
            >
              <span style={{ fontFamily: 'Inter Bold', fontWeight: 600 }}>
                From one trial order
              </span>
              <span
                style={{
                  fontFamily: 'Inter Bold',
                  fontWeight: 800,
                  fontSize: '34px',
                  letterSpacing: '-0.02em',
                }}
              >
                $81.60
              </span>
            </div>
            <div
              style={{
                fontFamily: 'Inter Regular',
                fontSize: '12px',
                color: INK,
                marginTop: '10px',
              }}
            >
              Illustrative example. Actual results vary.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeTopicThree;
